import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import { Formik, FormikProps, Form, FormikHelpers } from 'formik';
import get from 'lodash.get';
import merge from 'lodash.merge';

import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import TextField from '@material-ui/core/TextField';

import compose from 'helpers/compose';
import { scrollToTop } from 'helpers/helpers';
import FormNumberField from 'components/FormFields/FormNumberField';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import Background from 'components/Layout/Background';
import YearsAndMonths from 'components/Generic/YearsAndMonths';
import GridSubtitle from 'components/GridSubtitle/GridSubtitle';

import { AccountEditBasic, AccountBasic } from 'stores/workflowStores/workflowBasicStore/model';
import { Partner } from 'stores/workflowStores/finances/planningFor/model';
import { Income, getInitialValues } from 'stores/workflowStores/finances/currentIncome/model';
import { RootStore } from 'stores/rootStore';
import CurrentIncomeStore from 'stores/workflowStores/finances/currentIncome/currentIncomeStore';
import { useTwoColumns } from 'hooks/useTwoColumns';
import Info from 'components/Tooltip/Info';
import { MultiInputError } from 'components/MultiInputError/MultiInputError';
import { Currency } from 'enums/index';
import { RentModalsStore } from 'stores/workflowStores/finances/rentModalsStore';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface Props {
  accountId: string;
  rootStore: RootStore;
  rentModalsStore: RentModalsStore;
  currency: Currency;
  partner: Partner;
  basicData: AccountEditBasic | AccountBasic;
  detailMode?: boolean;
}

const CurrentIncome: React.FC<Props> = ({
  rootStore,
  accountId,
  partner,
  currency,
  basicData,
  rentModalsStore,
  detailMode
}) => {
  const [store] = useState(new CurrentIncomeStore(rootStore, accountId, rentModalsStore, detailMode));

  useEffect(() => {
    scrollToTop('.MuiDialogContent-root');
    store.fetchIncome();
  }, [store]);

  const { t } = useTranslation();
  const { data, handleSubmit, calcSubmit } = store;
  const { rentFormKey, setRentSubmitting } = rentModalsStore;

  const onSubmit = async (values: Income, helpers: FormikHelpers<Income>) => {
    setRentSubmitting(true);
    await handleSubmit(values, helpers);
    setRentSubmitting(false);
  };

  const { twoColumns } = useTwoColumns();

  const translatedCurrency = t(`currency_${currency}`);

  const renderMoreAbout = (formikProps: FormikProps<Income>, forPartner?: boolean) => {
    const prefix: keyof Income = forPartner ? 'partnerData' : 'clientData';
    const { retired } = formikProps.values[prefix]!;

    const handleOnChange = async () => {
      setRentSubmitting(true);
      await calcSubmit(formikProps, !!forPartner);
      setRentSubmitting(false);
    };

    const currentYrs = get(formikProps, `values.${prefix}.age.years`);
    const currentMnth = get(formikProps, `values.${prefix}.age.months`);

    const retirementAgeError = get(formikProps, `errors.${prefix}.plannedRetirementAge`);

    return (
      <Grid container>
        <Grid item xs={12}>
          <Typography variant="h3">
            {forPartner ? partner.firstname : get(basicData, 'client.firstname')}
          </Typography>
        </Grid>

        {retired && (
          <Grid item xs={12}>
            <FormNumberField
              endAdornment={translatedCurrency}
              name={`${prefix}.retirement.value`}
              label={t('finQuestionnary_rent_pension')}
              afterBlur={handleOnChange}
              tooltipContent={t('tooltip_currentIncome_pension')}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <FormNumberField
            endAdornment={translatedCurrency}
            name={`${prefix}.employment.value`}
            label={t('finQuestionnary_aboutFamily_grossWorkIncome')}
            afterBlur={handleOnChange}
            tooltipContent={t('tooltip_currentIncome_grossWorkIncome')}
          />
        </Grid>

        <Grid item xs={12}>
          <FormNumberField
            endAdornment={`${translatedCurrency} ${t('general_frequency_YEARLY')}`}
            name={`${prefix}.business.value`}
            label={t('finQuestionnary_aboutFamily_incomeExpenseDifference')}
            afterBlur={handleOnChange}
            tooltipContent={t('tooltip_currentIncome_incomeExpenseDifference')}
          />
        </Grid>

        <Grid item xs={12}>
          <FormNumberField
            endAdornment={translatedCurrency}
            name={`${prefix}.other.value`}
            label={t('finQuestionnary_aboutFamily_otherNetIncomes')}
            readOnly={formikProps.isSubmitting}
            afterBlur={handleOnChange}
            tooltipContent={t('tooltip_currentIncome_otherNetIncomes')}
          />
        </Grid>

        {!retired && (
          <Grid item xs={12}>
            <FormNumberField
              name={`${prefix}.children`}
              label={t('finQuestionnary_aboutFamily_kidsNumber')}
              afterBlur={handleOnChange}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <FormNumberField
            name={`${prefix}.netIncome.value`}
            label={t('finQuestionnary_aboutFamily_totalIncome')}
            endAdornment={translatedCurrency}
            readOnly
          />
          <TextField
            name={`${prefix}.netIncome.currency`}
            type="hidden"
            style={{ visibility: 'hidden' }}
            value={currency}
          />
        </Grid>

        {!retired && (
          <>
            <Grid item xs={12}>
              <Divider />
            </Grid>

            <GridSubtitle>
              {t('finQuestionnary_aboutFamily_retirenmentAge')}
              <Info content={t('tooltip_currentIncome_plannedRetirementAge')} heading />
            </GridSubtitle>

            <Grid item xs={6}>
              <FormNumberField
                fast={false}
                name={`${prefix}.plannedRetirementAge.years`}
                label={t('general_years')}
                error={!!retirementAgeError}
              />
            </Grid>

            <Grid item xs={6}>
              <FormNumberField
                fast={false}
                name={`${prefix}.plannedRetirementAge.months`}
                type="number"
                label={t('general_months')}
                error={!!retirementAgeError}
              />
            </Grid>

            {retirementAgeError && <MultiInputError error={t(retirementAgeError)} />}

            <Grid item xs={12}>
              {currentYrs ? (
                <Typography variant="subtitle1">
                  {t('finQuestionnary_aboutFamily_actualAge')}:{' '}
                  <YearsAndMonths data={{ years: currentYrs, months: currentMnth }} />
                </Typography>
              ) : null}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1">
                {t('finQuestionnary_aboutFamily_lawRetirenmentAge')}:{' '}
                <YearsAndMonths data={formikProps.values[prefix]!.calculatedRetirementAge} />.
              </Typography>
            </Grid>
          </>
        )}
      </Grid>
    );
  };

  const initialValues = getInitialValues(!!data?.partnerData, currency);

  return (
    data && (
      <Formik
        initialValues={merge({}, initialValues, data) as Income}
        onSubmit={onSubmit}
      >
        {(formikProps) => (
          <FormWithoutEnterSubmit id={rentFormKey}>
            <Background className={twoColumns} inside>
              {renderMoreAbout(formikProps)}
              {partner ? renderMoreAbout(formikProps, true) : null}
            </Background>
          </FormWithoutEnterSubmit>
        )}
      </Formik>
    )
  );
};

export default compose(inject('rootStore'), observer)(CurrentIncome);
